import React, { useState, useContext } from 'react';
import { CartContext } from '../context/CartContext';
import OrderSummary from './OrderSummary';
import { formatPhone, unformatPhone } from '../utils/phoneUtils';
import { useToast } from './ui/Toast';

const OrderForm = ({ onBack, onOrderComplete }) => {
  const { cart, getSubtotal } = useContext(CartContext);
  const { push } = useToast();
  const [showSummary, setShowSummary] = useState(false);
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [formData, setFormData] = useState({
    phone: '',
    paymentMethod: 'PIX',
    shippingType: 'PICKUP',
    notes: '',
    // Campos de endereço
    recipientName: '',
    zipCode: '',
    state: '',
    city: '',
    neighborhood: '',
    street: '',
    number: '',
    complement: ''
  });
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  // apenas texto de aviso; o valor do frete vem do backend (quote)
  const zipNumbers = formData.zipCode.replace(/\D/g, '');
  const isAnapolis = zipNumbers.startsWith('75');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbers = unformatPhone(value);
      const masked = formatPhone(value);
      
      // Armazena o número sem máscara no formData
      setPhoneDisplay(masked);
      setFormData({
        ...formData,
        phone: numbers
      });
    } else if (name === 'zipCode') {
      // Formata o CEP com máscara 00000-000
      const numbers = value.replace(/\D/g, '').slice(0, 8);
      let formatted = numbers;
      if (numbers.length > 5) {
        formatted = `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
      }
      setFormData({
        ...formData,
        zipCode: formatted
      });
      
      // Busca o endereço quando o CEP tem 8 dígitos
      if (numbers.length === 8) {
        fetchAddressByCep(numbers);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const fetchAddressByCep = async (cep) => {
    setIsLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          state: data.uf || '',
          city: data.localidade || '',
          neighborhood: data.bairro || '',
          street: data.logradouro || ''
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setIsLoadingCep(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.phone || !formData.paymentMethod) {
      push('Preencha o telefone para continuar.', 'danger');
      return;
    }

    // Valida campos de endereço se for entrega
    if (formData.shippingType === 'DELIVERY') {
      if (!formData.zipCode || !formData.state || !formData.city || !formData.neighborhood || !formData.street || !formData.number) {
        push('Preencha todos os campos de endereço obrigatórios.', 'danger');
        return;
      }
    }

    setShowSummary(true);
  };

  if (showSummary) {
    return <OrderSummary formData={formData} onBack={() => setShowSummary(false)} onOrderComplete={onOrderComplete} />;
  }

  return (
    <div className="min-h-screen bg-cream-100 py-4 sm:py-6 md:py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-ink-soft hover:text-ink mb-4 sm:mb-6 transition-colors touch-manipulation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm sm:text-base">Voltar ao Catálogo</span>
        </button>

        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 md:p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-4 sm:mb-6">Dados do Pedido</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">
                Telefone para Contato *
              </label>
              <input
                type="tel"
                name="phone"
                value={phoneDisplay}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                maxLength="15"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">
                Método de Pagamento *
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                required
              >
                <option value="PIX">Pix</option>
                {/* <option value="CREDIT_CARD">Cartão de Crédito</option>
                <option value="BOLETO">Boleto</option>
                <option value="OTHER">Outro</option> */}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">
                Tipo de Entrega *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, shippingType: 'PICKUP' })}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    formData.shippingType === 'PICKUP'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-cocoa-200 bg-white text-ink-soft hover:border-cocoa-300'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="text-sm sm:text-base font-medium">Retirada</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, shippingType: 'DELIVERY' })}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    formData.shippingType === 'DELIVERY'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-cocoa-200 bg-white text-ink-soft hover:border-cocoa-300'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm sm:text-base font-medium">Entrega</span>
                </button>
              </div>
            </div>

            {formData.shippingType === 'DELIVERY' && (
              <div className="bg-cream-50 rounded-lg p-4 sm:p-6 space-y-4">
                <h3 className="text-lg sm:text-xl font-semibold text-ink mb-2">Endereço de Entrega</h3>
                
                <div>
                  <label className="block text-sm font-semibold text-ink mb-1.5">
                    Nome do Destinatário
                  </label>
                  <input
                    type="text"
                    name="recipientName"
                    value={formData.recipientName}
                    onChange={handleChange}
                    placeholder="Nome de quem vai receber"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1.5">
                      CEP *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        placeholder="00000-000"
                        maxLength="9"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                        required={formData.shippingType === 'DELIVERY'}
                      />
                      {isLoadingCep && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-5 w-5 text-primary-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1.5">
                      Estado *
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="UF"
                      maxLength="2"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                      required={formData.shippingType === 'DELIVERY'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1.5">
                      Cidade *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Cidade"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                      required={formData.shippingType === 'DELIVERY'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1.5">
                      Bairro *
                    </label>
                    <input
                      type="text"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleChange}
                      placeholder="Bairro"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                      required={formData.shippingType === 'DELIVERY'}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-ink mb-1.5">
                    Rua *
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    placeholder="Nome da rua"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                    required={formData.shippingType === 'DELIVERY'}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1.5">
                      Número *
                    </label>
                    <input
                      type="text"
                      name="number"
                      value={formData.number}
                      onChange={handleChange}
                      placeholder="Nº"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                      required={formData.shippingType === 'DELIVERY'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1.5">
                      Complemento
                    </label>
                    <input
                      type="text"
                      name="complement"
                      value={formData.complement}
                      onChange={handleChange}
                      placeholder="Apto, Bloco..."
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                {/* Informação sobre taxa de entrega */}
                {zipNumbers.length === 8 && (
                  isAnapolis ? (
                    <div className="bg-sage-50 border border-sage-200 rounded-xl p-3 sm:p-4 flex items-start gap-3">
                      <svg className="w-5 h-5 text-sage-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-sage-700">Entrega em Anápolis-GO</p>
                        <p className="text-sm text-sage-600">A taxa de entrega aparece no resumo do pedido.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-honey-50 border border-honey-200 rounded-xl p-3 sm:p-4 flex items-start gap-3">
                      <svg className="w-5 h-5 text-honey-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-honey-700">Entrega por Transportadora</p>
                        <p className="text-sm text-honey-700">O valor do frete será informado após o fechamento do pedido.</p>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">
                Observações
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Informações adicionais sobre o pedido..."
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border border-cocoa-200 bg-white focus:border-cocoa-400 focus:ring-2 focus:ring-cocoa-200 focus:outline-none transition-colors resize-none"
              />
            </div>

            <div className="bg-cream-50 border border-cream-200 rounded-xl p-3 sm:p-4">
              <div className="flex justify-between items-center">
                <span className="text-base sm:text-lg font-semibold text-ink-soft">Subtotal (estimado):</span>
                <span className="text-xl sm:text-2xl font-bold text-primary-600">
                  R$ {getSubtotal().toFixed(2)}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-ink-muted mt-2">
                {cart.length} {cart.length === 1 ? 'item' : 'itens'} no carrinho · descontos e frete são calculados no resumo
              </p>
            </div>

            <button
              type="submit"
              className="w-full rounded-full bg-cocoa-800 px-6 py-3.5 text-base font-bold text-cream-50 transition-colors hover:bg-cocoa-900"
            >
              Revisar Pedido
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
